<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  provide,
  inject,
  getCurrentInstance,
} from 'vue';
import {loadStripe, http, snackbar} from '@teemill/common/services';
import {formatUrl, getShippingEstimate} from '@teemill/common/helpers';
import {toMinorUnits} from '@teemill/utilities';
import {usePaymentProcessorStore} from '@teemill/common/vuex';
import Stripe from '../../stripe/classes/stripe';
import CartConfirmShipping from './CartConfirmShipping.vue';
import isEmpty from 'lodash/isEmpty';
import {captureException} from '@sentry/vue';
import {useStore} from 'vuex';
import {useRouter} from 'vue-router';

const props = defineProps({
  defaultTotal: Number,
  name: {
    type: String,
    required: true,
  },
  routePrefix: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['click', 'rendered', 'closed']);

const store = useStore();
const router = useRouter();
const instance = getCurrentInstance();
const theme = inject('theme');

const expressCheckoutRef = ref(null);
const stripeInstance = ref(null);
const elements = ref(null);
const expressCheckoutElement = ref(null);
const hasPaymentMethods = ref(false);
const loading = ref(false);
const cardActionLoading = ref(false);
const checkoutResource = ref({});
const currentShippingRateId = ref(null);
const pendingConfirmationTokenId = ref(null);

let unmounted = false;

function destroyExpressCheckoutElement() {
  if (expressCheckoutElement.value) {
    expressCheckoutElement.value.destroy();
    expressCheckoutElement.value = null;
  }
}

const currency = computed(
  () => store?.state?.subdomain?.currency?.toLowerCase() ?? 'gbp'
);

const stripeAmount = computed(() =>
  toMinorUnits(props.defaultTotal, currency.value)
);

const preSelectedShipping = computed(() => {
  const methodId = checkoutResource.value?.checkoutData?.shipping_method;

  if (methodId === null || methodId === undefined) {
    return null;
  }

  return checkoutResource.value?.shippingMethods?.find(
    method => method.value === methodId
  );
});

function getShippingMethodLabel(method) {
  if (theme?.().get('checkout.shipping.showEstimates') !== '1') {
    return method.text;
  }
  return `${method.text} ${getShippingEstimate(
    method?.earliestShippingEstimate,
    method?.latestShippingEstimate
  )}`;
}

function formatShippingRates(methods) {
  return methods.map(method => ({
    id: method.value.toString(),
    displayName: getShippingMethodLabel(method),
    amount: toMinorUnits(method.price, currency.value),
  }));
}

function isValidShippingMethodId(id) {
  return id !== null && id !== undefined && /^\d+$/.test(String(id));
}

function resolveShippingMethodId(event) {
  const rateId = event.shippingRate?.id;

  if (isValidShippingMethodId(rateId)) {
    return String(rateId);
  }

  if (isValidShippingMethodId(currentShippingRateId.value)) {
    return String(currentShippingRateId.value);
  }

  return null;
}

async function loadShippingRatesForAddress(address) {
  if (!address?.country) {
    return null;
  }

  currentShippingRateId.value = null;

  const updateResponse = await http.post(
    formatUrl(`/omnis/v3/checkout/${props.name}/update-country-and-state/`),
    {
      country: address.country,
      state: address.state || null,
      act_as_billing_info: true,
    }
  );

  const methods = updateResponse?.data?.shippingMethods;

  if (!methods?.length) {
    return null;
  }

  const firstMethod = methods[0];
  currentShippingRateId.value = firstMethod.value.toString();

  const newTotal = updateResponse.data.summary.total + firstMethod.price;

  elements.value.update({
    amount: toMinorUnits(newTotal, currency.value),
  });

  return formatShippingRates(methods);
}

function parseNameFields(name) {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);

  if (parts.length <= 1) {
    return {first_name: parts[0] ?? '', last_name: ''};
  }

  return {
    first_name: parts.slice(0, -1).join(' '),
    last_name: parts[parts.length - 1],
  };
}

function extractBillingInfo(event) {
  const billing = event.billingDetails || {};
  const shipping = event.shippingAddress || {};
  const address = shipping.address || billing.address || {};
  const {first_name, last_name} = parseNameFields(
    shipping.name || billing.name
  );

  return {
    first_name: first_name ?? '',
    last_name: last_name ?? '',
    email: billing.email,
    company: null,
    phone: billing.phone || null,
    country: address.country,
    state: address.state,
    street_address: [address.line1, address.line2].filter(Boolean).join(', '),
    city: address.city,
    zip: address.postal_code,
    billing_is_shipping: true,
    act_as_billing_info: true,
    return_resource: true,
  };
}

function formatBillingForStripe(billingInfo) {
  const name = `${billingInfo.first_name ?? ''} 
    ${billingInfo.last_name ?? ''}`.trim();

  return {
    name,
    email: billingInfo.email,
    phone: billingInfo.phone || '',
    address: {
      line1: billingInfo.street_address,
      line2: null,
      city: billingInfo.city,
      state: billingInfo.state,
      postal_code: billingInfo.zip,
      country: billingInfo.country,
    },
  };
}

async function handlePaymentSuccess(data, stripe) {
  if (typeof data === 'object' && data.requires_action) {
    cardActionLoading.value = true;

    sessionStorage.setItem('stripe_order_hash', data.order_hash);
    sessionStorage.setItem('stripe_storefront_name', props.name);
    sessionStorage.setItem('stripe_route_prefix', props.routePrefix);

    const {error: actionError} = await stripeInstance.value.handleNextAction({
      clientSecret: data.payment_intent_client_secret,
    });

    if (actionError) {
      stripe.handleFailedResponse(actionError);
      instance?.proxy?.$overlays?.close(
        'express-checkout-confirm-shipping-popup'
      );
      cardActionLoading.value = false;
      return;
    }

    let confirmResponse;
    await http
      .post(
        formatUrl(
          `${props.routePrefix}${props.name}/stripe-quick-pay-confirm/`
        ),
        {order_hash: data.order_hash}
      )
      .success(confirmData => {
        confirmResponse = confirmData;
      })
      .oops(
        'Unable to confirm your payment. Please check your email for order confirmation.'
      );

    if (confirmResponse && confirmResponse.status === 'succeeded') {
      store.commit('cart/clear');
      router.push({path: `/receipt/${data.order_hash}/`});
    } else {
      cardActionLoading.value = false;
    }
  } else {
    const orderHash = typeof data === 'string' ? data : data?.order_hash;
    store.commit('cart/clear');
    router.push({path: `/receipt/${orderHash}/`});
  }
}

async function confirmFromPopup(checkoutData, shippingMethod) {
  if (!pendingConfirmationTokenId.value) {
    snackbar.error(
      'Please close this popup and retry payment with express checkout again.'
    );
    return;
  }

  loading.value = true;

  try {
    const stripe = new Stripe(stripeInstance.value);

    let payResponse;
    await http
      .post(formatUrl(`${props.routePrefix}${props.name}/stripe-quick-pay/`), {
        confirmation_token_id: pendingConfirmationTokenId.value,
        shipping_method: shippingMethod,
        billing_info: checkoutData.billing_info,
      })
      .success(data => {
        payResponse = data;
      })
      .validation(message => {
        snackbar.error(message);
      })
      .handle(409, error => {
        onUnseenMessages(error.message);
      })
      .handle(423, error => {
        snackbar.error(error.message);
        setTimeout(() => location.reload(), 500);
      })
      .handle([400, 402], error => {
        pendingConfirmationTokenId.value = null;
        stripe.handleFailedResponse(error);
      })
      .oops(
        'Unable to process your card payment at this time, try again later.'
      );

    if (!payResponse) {
      return;
    }

    pendingConfirmationTokenId.value = null;
    await handlePaymentSuccess(payResponse, stripe);
  } catch {
    snackbar.error('Unable to process your payment. Please try again.');
  } finally {
    loading.value = false;
  }
}

provide('confirm', confirmFromPopup);

function showConfirmShippingPopup(billingInfo) {
  instance?.proxy?.$overlays?.open('express-checkout-confirm-shipping-popup');
  loading.value = true;

  http
    .post(
      formatUrl(`/omnis/v3/checkout/${props.name}/update-checkout/`),
      billingInfo
    )
    .success(data => {
      handleCheckoutResource(data);
    })
    .any(() => {
      snackbar.error(
        'Unable to process your card payment at this time, try again later.'
      );
    })
    .finally(() => {
      loading.value = false;
    });
}

function handleCheckoutResource(data) {
  checkoutResource.value = data;

  if (data.hasUnseenMessages) {
    onUnseenMessages();
  }
}

function onUnseenMessages(message) {
  snackbar.error(
    message || 'Some of the items in your bag are low or out of stock'
  );
  instance?.proxy?.$overlays?.close('express-checkout-confirm-shipping-popup');
}

async function buildElement() {
  stripeInstance.value = await loadStripe(
    usePaymentProcessorStore().getter('stripeAccountId')
  );

  if (unmounted) return;

  if (!stripeInstance.value) return;

  elements.value = stripeInstance.value.elements({
    mode: 'payment',
    amount: stripeAmount.value,
    currency: currency.value,
    paymentMethodCreation: 'manual',
  });

  if (unmounted) {
    elements.value = null;
    return;
  }

  const expressCheckout = elements.value.create('expressCheckout', {
    buttonHeight: 46,
    buttonType: {
      applePay: 'plain',
      googlePay: 'plain',
      payPal: 'paypal',
    },
    layout: {
      maxColumns: 1,
      overflow: 'never',
    },
    shippingAddressRequired: true,
    emailRequired: true,
    phoneNumberRequired: true,
  });

  if (unmounted) {
    expressCheckout.destroy();
    elements.value = null;
    return;
  }

  expressCheckoutElement.value = expressCheckout;

  expressCheckoutElement.value.on('ready', ({availablePaymentMethods}) => {
    if (availablePaymentMethods) {
      hasPaymentMethods.value = true;
      emit('rendered');
    }
  });

  expressCheckoutElement.value.on('click', ({resolve, reject}) => {
    emit('click');

    if (loading.value) {
      reject();
      return;
    }

    resolve();
  });

  expressCheckoutElement.value.on(
    'shippingaddresschange',
    async ({address, resolve, reject}) => {
      try {
        const shippingRates = await loadShippingRatesForAddress(address);

        if (!shippingRates?.length) {
          reject();
          return;
        }

        resolve({shippingRates});
      } catch {
        reject();
      }
    }
  );

  expressCheckoutElement.value.on(
    'shippingratechange',
    async ({shippingRate, resolve, reject}) => {
      if (!shippingRate || !isValidShippingMethodId(shippingRate.id)) {
        reject();
        return;
      }

      try {
        const response = await http.post(
          formatUrl(`/omnis/v3/checkout/${props.name}/update-shipping/`),
          {shipping_method: shippingRate.id}
        );

        if (!response?.data) {
          reject();
          return;
        }

        const newTotal =
          response.data.summary.total + response.data.summary.shipping;

        currentShippingRateId.value = shippingRate.id;

        elements.value.update({
          amount: toMinorUnits(newTotal, currency.value),
        });

        resolve();
      } catch {
        reject();
      }
    }
  );

  expressCheckoutElement.value.on('cancel', () => {
    currentShippingRateId.value = null;
    pendingConfirmationTokenId.value = null;
    elements.value.update({amount: stripeAmount.value});
  });

  expressCheckoutElement.value.on('confirm', async event => {
    loading.value = true;
    pendingConfirmationTokenId.value = null;

    const billingInfo = extractBillingInfo(event);
    const shippingMethod = resolveShippingMethodId(event);
    const stripe = new Stripe(stripeInstance.value);

    try {
      if (!shippingMethod) {
        event.paymentFailed({reason: 'fail'});
        snackbar.error(
          'Shipping options are still loading. Please wait a moment and try again.'
        );
        loading.value = false;
        return;
      }

      const {error: submitError} = await elements.value.submit();
      if (submitError) {
        event.paymentFailed({reason: 'fail'});
        snackbar.error(
          submitError.message ?? 'Please check your payment details.'
        );
        loading.value = false;
        return;
      }

      const formattedBilling = formatBillingForStripe(billingInfo);

      const {confirmationToken, error: tokenError} =
        await stripeInstance.value.createConfirmationToken({
          elements: elements.value,
          params: {
            payment_method_data: {
              billing_details: formattedBilling,
            },
          },
        });

      if (tokenError) {
        event.paymentFailed({reason: 'fail'});
        stripe.handleFailedResponse(tokenError);
        loading.value = false;
        return;
      }

      pendingConfirmationTokenId.value = confirmationToken.id;

      let payResponse;
      await http
        .post(
          formatUrl(`${props.routePrefix}${props.name}/stripe-quick-pay/`),
          {
            confirmation_token_id: pendingConfirmationTokenId.value,
            shipping_method: shippingMethod,
            billing_info: billingInfo,
          }
        )
        .success(data => {
          payResponse = data;
        })
        .validation(() => {
          showConfirmShippingPopup(billingInfo);
        })
        .handle(409, error => {
          onUnseenMessages(error.message);
        })
        .handle(423, error => {
          snackbar.error(error.message);
          setTimeout(() => location.reload(), 500);
        })
        .handle([400, 402], error => {
          event.paymentFailed({reason: 'fail'});
          stripe.handleFailedResponse(error);
        })
        .oops(() => {
          event.paymentFailed({reason: 'fail'});
          snackbar.error(
            'Unable to process your card payment at this time, try again later.'
          );
        });

      if (!payResponse) {
        loading.value = false;
        return;
      }

      pendingConfirmationTokenId.value = null;
      await handlePaymentSuccess(payResponse, stripe);
      loading.value = false;
    } catch (err) {
      captureException(err);
      event.paymentFailed({reason: 'fail'});
      loading.value = false;
      snackbar.error('Unable to process your payment. Please try again.');
    }
  });

  if (unmounted) {
    destroyExpressCheckoutElement();
    return;
  }

  if (expressCheckoutRef.value) {
    expressCheckoutElement.value.mount(expressCheckoutRef.value);
  }
}

watch(
  () => props.defaultTotal,
  newValue => {
    if (newValue && elements.value) {
      elements.value.update({
        amount: toMinorUnits(newValue, currency.value),
      });
    }
  }
);

onMounted(() => {
  buildElement();
});

onBeforeUnmount(() => {
  unmounted = true;
  pendingConfirmationTokenId.value = null;
  destroyExpressCheckoutElement();
});
</script>

<template>
  <div>
    <div
      class="stripe-payments"
      :style="{visibility: hasPaymentMethods ? 'visible' : 'hidden'}"
    >
      <div ref="expressCheckoutRef" />
    </div>

    <tml-new-popup
      name="express-checkout-confirm-shipping-popup"
      layout="standard"
      size="small"
      height="auto"
      title="Confirm order"
      @leave="emit('closed')"
    >
      <template #content>
        <cart-confirm-shipping
          v-if="!isEmpty(checkoutResource)"
          :checkout-resource="checkoutResource"
          :pre-selected-shipping="preSelectedShipping"
          :name="name"
          :loading="loading || cardActionLoading"
          @unseen-messages="onUnseenMessages"
        />
      </template>
    </tml-new-popup>
  </div>
</template>
